import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FieldsService } from 'src/app/services/fields.service';
import { UtilityService } from 'src/app/services/utility.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { NgForm } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import moment from 'moment';
import { LoggerService } from '../../../services/logger.service';

export interface ScheduleFirstLessonData {
  studentId?: number;
  studentName?: string;
  inquiryId?: number;
  inquiryDate?: string;
  inquiryTaker?: number;
  enabled?: boolean;
  executive?: number;
  teacher?: number;
  lessonType?: string;
  dateTime?: string | Date;
  showScheduleToggle?: boolean;
}

export interface ScheduleFirstLessonSubmitData extends ScheduleFirstLessonData {
  formattedDateTime?: string;
  student?: any;
  duration?: number;
}

@Component({
  selector: 'app-schedule-first-lesson',
  templateUrl: './schedule-first-lesson.component.html',
  styleUrls: ['./schedule-first-lesson.component.css']
})
export class ScheduleFirstLessonComponent implements OnInit {
  @Input() scheduleData: ScheduleFirstLessonData = {};
  @Input() hideSubmitButton: boolean = false;
  @Input() hideScheduleToggle: boolean = false;
  @Input() allowedLessonTypeNames: string[] = [];
  @Output() scheduleDataChange = new EventEmitter<ScheduleFirstLessonData>();
  @Output() conflictFound = new EventEmitter<boolean>();
  @Output() onSubmitSchedule = new EventEmitter<ScheduleFirstLessonSubmitData>();
  @Output() onSuccess = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<any>();

  // Variables for package handling
  private DEPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalPackageSubject = new ReplaySubject<any>(1);
  private PreOriginalCouplePackageSubject = new ReplaySubject<any>(1);

  // Public observables to expose the subjects
  public DEPackage$ = this.DEPackageSubject.asObservable();
  public PreOriginalPackage$ = this.PreOriginalPackageSubject.asObservable();
  public PreOriginalCouplePackage$ = this.PreOriginalCouplePackageSubject.asObservable();

  enabled: boolean = false;
  field_date_and_time: string;
  hasConflicts: boolean = false;
  disableSubmitButton: boolean = false;
  errorMessage: string = '';
  showProgressSpinner: boolean = false;

  constructor(
    public _fieldsService: FieldsService,
    private _utilityService: UtilityService,
    private _taxonomyService: TaxonomyService,
    private _drupalRESTService: DrupalRESTService,
    private _entityRESTService: EntityRESTService,
    private _authService: AuthService
  ) { }

  ngOnInit(): void {
    // Initialize from input data or set defaults
    this.enabled = this.scheduleData.enabled || false;

    // Initialize date and time if provided, otherwise default to next hour rounded to 15 minutes
    if (this.scheduleData.dateTime) {
      this.field_date_and_time = typeof this.scheduleData.dateTime === 'string'
        ? this.scheduleData.dateTime
        : moment(this.scheduleData.dateTime).format('YYYY-MM-DD[T]HH:mm:ss');
    } else {
      this.field_date_and_time = this.roundToNearest15Min(moment().add(1, 'hour')).format('YYYY-MM-DD[T]HH:mm:ss');
    }
  }

  /**
   * Rounds the given time to the nearest 15 minute interval
   */
  roundToNearest15Min(dateTime: moment.Moment = moment()): moment.Moment {
    const minutes = dateTime.minutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    return dateTime.clone().minutes(roundedMinutes).seconds(0).milliseconds(0);
  }

  /**
   * Update the date and time when selected in the picker
   */
  uiUpdateDate(event: any): void {
    // Store raw moment object
    if (event) {
      this.field_date_and_time = moment(event).format('YYYY-MM-DD[T]HH:mm:ss');
      this.emitChanges();
      this.checkScheduleConflicts();
    }
  }

  /**
   * Check for scheduling conflicts with the selected teacher and time
   */
  checkScheduleConflicts(): void {
    if (!this.enabled || !this.scheduleData.teacher || !this.field_date_and_time || !this.scheduleData.lessonType) {
      return;
    }

    // Here you would call your conflict checking service/method
    // This should be implemented based on your actual conflict checking logic
    // For example:
    // this._schedulingService.checkConflicts(
    //   this.scheduleData.teacher,
    //   this.field_date_and_time,
    //   this.updateLessonDefaultTimes(this.scheduleData.lessonType)
    // ).subscribe(conflicts => {
    //   this.hasConflicts = conflicts.length > 0;
    //   this.conflictFound.emit(this.hasConflicts);
    // });

    // For now, we're just setting a default value
    this.hasConflicts = false;
    this.conflictFound.emit(this.hasConflicts);
  }

  /**
   * Get the default duration for a lesson type
   */
  updateLessonDefaultTimes(lessonTypeId: string): number {
    if (!lessonTypeId) {
      return 30; // Default to 30 minutes
    }

    // This would need to be implemented based on your system's logic
    // Look up the lesson type and return the appropriate duration
    const lessonType = this._taxonomyService.lesson_type?.find(lt => lt.id === lessonTypeId);

    // If we find the lesson type and it has a duration property, use it
    if (lessonType && lessonType.duration) {
      return lessonType.duration;
    }

    // Default durations based on lesson type IDs from the system
    switch (lessonTypeId) {
      case '560': // Dance Evaluation
        return 45;
      case '1393': // Pre-Original Program
        return 60;
      case '1429': // Pre-Original (Couple) - real lesson type
        return 60;
      default:
        return 30;
    }
  }

  /**
   * Get filtered lesson types based on your business logic
   */
  getFilteredLessonTypes(): any[] {
    const allLessonTypes = this._taxonomyService.lesson_type || [];

    // If no filter is provided, return all lesson types
    if (!this.allowedLessonTypeNames || this.allowedLessonTypeNames.length === 0) {
      return allLessonTypes;
    }

    // Filter lesson types to only include those in the allowedLessonTypeIds array (treating as names)
    return allLessonTypes.filter(lessonType =>
      this.allowedLessonTypeNames.includes(lessonType.name)
    );
  }

  /**
   * Handle changes to the schedule toggle
   */
  onScheduleToggleChange(): void {
    this.scheduleData.enabled = this.enabled;
    this.emitChanges();
  }

  /**
   * Handle changes to the executive selection
   */
  onExecutiveChange(value: number): void {
    this.scheduleData.executive = value;
    this.emitChanges();
  }

  /**
   * Handle changes to the teacher selection
   */
  onTeacherChange(value: number): void {
    this.scheduleData.teacher = value;
    this.emitChanges();
    this.checkScheduleConflicts();
  }

  /**
   * Handle changes to the lesson type selection
   */
  onLessonTypeChange(value: string): void {
    this.scheduleData.lessonType = value;
    this.emitChanges();
    this.checkScheduleConflicts();
  }

  /**
   * Validate the form data before submission
   */
  isValid(): boolean {
    if (!this.enabled) {
      return true; // If scheduling is disabled, no validation needed
    }

    // Check required fields
    if (!this.scheduleData.executive) {
      this.errorMessage = 'Please select an executive';
      return false;
    }

    if (!this.scheduleData.teacher) {
      this.errorMessage = 'Please select a teacher';
      return false;
    }

    if (!this.scheduleData.lessonType) {
      this.errorMessage = 'Please select a lesson type';
      return false;
    }

    if (!this.field_date_and_time) {
      this.errorMessage = 'Please select a date and time';
      return false;
    }

    if (this.hasConflicts) {
      this.errorMessage = 'Please resolve schedule conflicts before submitting';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  /**
   * Fetch system packages needed for special enrollments
   */
  async pullSystemPackages(): Promise<void> {
    try {
      // Fetch DE package
      const DEPackageResponse = await this._drupalRESTService.httpGET('/api_rest/v1/packages?parameters[field_package_label]=DE').toPromise();

      if (DEPackageResponse && Array.isArray(DEPackageResponse) && DEPackageResponse.length > 0) {
        this.DEPackageSubject.next(DEPackageResponse[0]);
      } else {
        this.DEPackageSubject.next(null);
      }

      // Fetch Pre-Original package
      const PreOriginalPackageResponse = await this._drupalRESTService.httpGET('/api_rest/v1/packages?parameters[field_package_label]=PreOriginal').toPromise();

      if (PreOriginalPackageResponse && Array.isArray(PreOriginalPackageResponse) && PreOriginalPackageResponse.length > 0) {
        this.PreOriginalPackageSubject.next(PreOriginalPackageResponse[0]);
      } else {
        this.PreOriginalPackageSubject.next(null);
      }

      // Fetch Pre-Original Couple package using the system packages endpoint
      const systemPackagesResponse = await this._drupalRESTService.httpGET('/api/v1/packages', [
        { parameter: "field_retired_op", value: "=" },
        { parameter: "field_retired_value", value: "0" },
        { parameter: "field_is_system_package_op", value: "=" },
        { parameter: "field_is_system_package_value", value: "1" },
      ]).toPromise();

      // Several system packages can share an SPS code + lesson type, and
      // studios rename them freely, so the oldest match is the seeded one.
      const PreOriginalCouplePackage = [...(systemPackagesResponse?.['results'] ?? [])]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .find(pkg => pkg.field_sps_code === 'PORI' && pkg.field_lesson_type === 'Pre Original (Couple)') ?? null;

      this.PreOriginalCouplePackageSubject.next(PreOriginalCouplePackage);

    } catch (error) {
      LoggerService.error('Error fetching system packages:', error);
      this.DEPackageSubject.next(null);
      this.PreOriginalPackageSubject.next(null);
      this.PreOriginalCouplePackageSubject.next(null);
      throw error;
    }
  }

  /**
   * Create a Dance Evaluation enrollment
   */
  async createDEEnrollment(createEnrollment: boolean, scheduleFirstLesson: boolean, studentData: any, scheduledDateTime: string, DEPackage: any): Promise<any> {
    if (!createEnrollment || !scheduleFirstLesson) return null;

    try {
      // Prepare the enrollment data
      const enrollmentData = {
        field_student: [studentData.id],
        field_teacher: [studentData.field_teacher],
        field_executive: [studentData.field_executive || studentData.schedule_first_lesson_field_executive],
        field_lesson_type: [studentData.lesson_type],
        field_package: [DEPackage.id],
        field_date: moment(scheduledDateTime).format('YYYY-MM-DD'),
        field_time: moment(scheduledDateTime).format('HH:mm:ss'),
        field_duration: this.updateLessonDefaultTimes(studentData.lesson_type),
        field_note: "Scheduled during student creation",
        field_studio_reference: [this._authService.selectedStudio?.id],
      };

      return await this._entityRESTService.createEntity('enrollments', 'enrollment', enrollmentData).toPromise();
    } catch (error) {
      LoggerService.error('Error creating DE enrollment:', error);
      throw error;
    }
  }

  /**
   * Create a Pre-Original Program enrollment
   */
  async createPreOriginalEnrollment(createEnrollment: boolean, scheduleFirstLesson: boolean, studentData: any, scheduledDateTime: string, PreOriginalPackage: any): Promise<any> {
    if (!createEnrollment || !scheduleFirstLesson) return null;

    try {
      // Prepare the enrollment data
      const enrollmentData = {
        field_student: [studentData.id],
        field_teacher: [studentData.field_teacher],
        field_executive: [studentData.field_executive || studentData.schedule_first_lesson_field_executive],
        field_lesson_type: [studentData.lesson_type],
        field_package: [PreOriginalPackage.id],
        field_date: moment(scheduledDateTime).format('YYYY-MM-DD'),
        field_time: moment(scheduledDateTime).format('HH:mm:ss'),
        field_duration: this.updateLessonDefaultTimes(studentData.lesson_type),
        field_note: "Scheduled during student creation",
        field_studio_reference: [this._authService.selectedStudio?.id],
      };

      return await this._entityRESTService.createEntity('enrollments', 'enrollment', enrollmentData).toPromise();
    } catch (error) {
      LoggerService.error('Error creating Pre-Original enrollment:', error);
      throw error;
    }
  }

  /**
   * Create a Pre-Original Couple Program enrollment
   */
  async createPreOriginalCoupleEnrollment(createEnrollment: boolean, scheduleFirstLesson: boolean, studentData: any, scheduledDateTime: string, PreOriginalCouplePackage: any): Promise<any> {
    if (!createEnrollment || !scheduleFirstLesson) return null;

    try {
      // Prepare the enrollment data
      const enrollmentData = {
        field_student: [studentData.id],
        field_teacher: [studentData.field_teacher],
        field_executive: [studentData.field_executive || studentData.schedule_first_lesson_field_executive],
        field_lesson_type: [studentData.lesson_type],
        field_package: [PreOriginalCouplePackage.id],
        field_date: moment(scheduledDateTime).format('YYYY-MM-DD'),
        field_time: moment(scheduledDateTime).format('HH:mm:ss'),
        field_duration: this.updateLessonDefaultTimes(studentData.lesson_type),
        field_note: "Scheduled during student creation",
        field_studio_reference: [this._authService.selectedStudio?.id],
      };

      return await this._entityRESTService.createEntity('enrollments', 'enrollment', enrollmentData).toPromise();
    } catch (error) {
      LoggerService.error('Error creating Pre-Original Couple enrollment:', error);
      throw error;
    }
  }

  /**
   * Handle form submission
   */
  async submitSchedule(student: any): Promise<void> {
    if (!this.isValid()) {
      return;
    }

    this.disableSubmitButton = true;
    this.showProgressSpinner = true;

    try {
      // Format the date and time for submission
      const formattedDateTime = moment(this.field_date_and_time).format('YYYY-MM-DDTHH:mm:ss');
      const duration = this.updateLessonDefaultTimes(this.scheduleData.lessonType);

      // Prepare the data for submission
      const submitData: ScheduleFirstLessonSubmitData = {
        ...this.scheduleData,
        formattedDateTime,
        student,
        duration
      };

      // Check if this is a special enrollment type
      const create_de_enrollment = Boolean(this.enabled && this.scheduleData.lessonType === '560');
      const create_preoriginal_enrollment = Boolean(this.enabled && this.scheduleData.lessonType === '1393');
      const create_preoriginal_couple_enrollment = Boolean(this.enabled && this.scheduleData.lessonType === '1429');

      // Fetch packages if needed for special enrollments
      if (create_de_enrollment || create_preoriginal_enrollment || create_preoriginal_couple_enrollment) {
        await this.pullSystemPackages();
      }

      // Emit the event with the submit data
      this.onSubmitSchedule.emit(submitData);

      // If student data is provided, handle enrollments
      if (student && student.id) {
        // Structure the student body for enrollment creation
        const studentBody = {
          id: student.id,
          field_teacher: this.scheduleData.teacher,
          field_executive: this.scheduleData.executive,
          lesson_type: this.scheduleData.lessonType
        };

        // Create Dance Evaluation enrollment if applicable
        if (create_de_enrollment) {
          await new Promise<void>((resolve, reject) => {
            this.DEPackage$.subscribe(
              async (DE_package) => {
                if (DE_package) {
                  try {
                    await this.createDEEnrollment(true, true, studentBody, formattedDateTime, DE_package);
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating DE enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('DE package not found');
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving DE package:', error);
                reject(error);
              }
            );
          });
        }

        // Create Pre-Original Program enrollment if applicable
        if (create_preoriginal_enrollment) {
          await new Promise<void>((resolve, reject) => {
            this.PreOriginalPackage$.subscribe(
              async (PreOriginal_package) => {
                if (PreOriginal_package) {
                  try {
                    await this.createPreOriginalEnrollment(true, true, studentBody, formattedDateTime, PreOriginal_package);
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating pre-original enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('Pre-original package not found');
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving pre-original package:', error);
                reject(error);
              }
            );
          });
        }

        // Create Pre-Original Couple enrollment if applicable
        if (create_preoriginal_couple_enrollment) {
          await new Promise<void>((resolve, reject) => {
            this.PreOriginalCouplePackage$.subscribe(
              async (PreOriginalCouple_package) => {
                if (PreOriginalCouple_package) {
                  try {
                    await this.createPreOriginalCoupleEnrollment(true, true, studentBody, formattedDateTime, PreOriginalCouple_package);
                    resolve();
                  } catch (error) {
                    LoggerService.error('Error creating pre-original couple enrollment:', error);
                    reject(error);
                  }
                } else {
                  LoggerService.log('Pre-original couple package not found');
                  resolve();
                }
              },
              (error) => {
                LoggerService.error('Error retrieving pre-original couple package:', error);
                reject(error);
              }
            );
          });
        }
      }

      this.onSuccess.emit(true);
    } catch (error) {
      LoggerService.error('Error submitting schedule:', error);
      this.errorMessage = 'An error occurred while scheduling the lesson';
      this.onError.emit(error);
    } finally {
      this.disableSubmitButton = false;
      this.showProgressSpinner = false;
    }
  }

  /**
   * Emit changes to parent component
   */
  private emitChanges(): void {
    // Update date time in the data object
    this.scheduleData.dateTime = this.field_date_and_time;
    this.scheduleDataChange.emit({ ...this.scheduleData });
  }
}
